import { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CgChevronDown } from "react-icons/cg";
import "./InvestorSidebar.styles.css";

const ACTIVE_COLOR = "#68C5EA"; 

const InvestorSidebar = ({ className = "" }) => {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();

  // breakpoint match
  const isDesktop =
    typeof window !== "undefined" &&
    window.matchMedia("(min-width: 1024px)").matches;

  // /tr/... /en/... gibi dil prefix'lerini düşür
  const normPath = useMemo(() => {
    const parts = pathname.split("/").filter(Boolean);
    if (parts.length >= 2 && parts[0].length === 2) {
      return "/" + parts.slice(1).join("/");
    }
    return pathname;
  }, [pathname]);

  const q = useMemo(() => new URLSearchParams(search), [search]);

  // route grupları
  const routeGroups = useMemo(() => {
    const IPO = new Set([
      "/investor-relations/initial-public-offering-new",
      "/yatirimci-iliskileri/halka-arz-yeni",
      "/investor-relations/initial-public-offering",
      "/yatirimci-iliskileri/halka-arz",
      // "/investor-relations/analyst-reports",
      // "/yatirimci-iliskileri/analist-raporlari",
    ]);
    const CORP = new Set([
      "/investor-relations/board-of-directors",
      "/yatirimci-iliskileri/yonetim-kurulu",
      "/investor-relations/executive-committee",
      "/yatirimci-iliskileri/icra-kurulu",
      "/investor-relations/board-committees",
      "/yatirimci-iliskileri/yonetim-kurulu-komiteleri",
      "/investor-relations/trade-registry",
      "/yatirimci-iliskileri/ticaret-sicili",
      "/investor-relations/general-assembly",
      "/yatirimci-iliskileri/genel-kurul",
      "/investor-relations/corporate-governance-report",
      "/yatirimci-iliskileri/kurumsal-yonetim-raporu",
      "/investor-relations/articles-of-association",
      "/yatirimci-iliskileri/esas-sozlesme",
      "/investor-relations/capital-structure",
      "/yatirimci-iliskileri/sermaye-yapisi",
      "/investor-relations/policies",
      "/yatirimci-iliskileri/politikalar",
    ]);
    const REPORTS = new Set([
      "/investor-relations/reports",
      "/yatirimci-iliskileri/raporlar",
    ]);
    const FIN = new Set([
      "/investor-relations/financial-results",
      "/yatirimci-iliskileri/finansal-sonuclar",
    ]);
    const PRES = new Set([
      "/investor-relations/investor-presentations",
      "/yatirimci-iliskileri/yatirimci-sunumlari",
    ]);
    const SPEC = new Set([
      "/investor-relations/special-announcements",
      "/yatirimci-iliskileri/ozel-durum-aciklamalari",
    ]);
    const PRESS = new Set([
      "/investor-relations/press-announcements",
      "/yatirimci-iliskileri/basin-duyurulari",
    ]);
    const FAQ = new Set([
      "/investor-relations/faq",
      "/yatirimci-iliskileri/sikca-sorulan-sorular",
    ]);

    return { IPO, CORP, REPORTS, FIN, PRES, SPEC, PRESS, FAQ };
  }, []);

  // aktif alt grup
  const activeGroupKey = useMemo(() => {
    const p = normPath;
    if (routeGroups.IPO.has(p)) return "ipo";
    if (routeGroups.CORP.has(p)) return "corp";
    if (routeGroups.REPORTS.has(p)) return "reports";
    if (routeGroups.FIN.has(p)) return "fin";
    if (routeGroups.PRES.has(p)) return "pres";
    if (routeGroups.SPEC.has(p)) return "announcements";
    if (routeGroups.PRESS.has(p)) return "announcements";
    if (routeGroups.FAQ.has(p)) return "faq";
    return null;
  }, [normPath, routeGroups]);

  // açık/kapalı state
  const [openSections, setOpenSections] = useState(() => ({
    ir: isDesktop,
    ipo: false,
    corp: false,
    reports: false,
  }));

  // breakpoint dinleyicisi
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => {
      setOpenSections((prev) => ({ ...prev, ir: e.matches }));
    };
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // rota değişince ilgili alt grubu otomatik aç
  useEffect(() => {
    setOpenSections((prev) => ({
      ...prev,
      ir: prev.ir || isDesktop,
      ipo: activeGroupKey === "ipo",
      corp: activeGroupKey === "corp",
      reports: activeGroupKey === "reports",
    }));
  }, [activeGroupKey, isDesktop]);

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // aktiflik helper'ları (sadece yazıyı mavi yapacağız)
  const isActivePath = (paths) => paths.some((p) => p === normPath);
  const styleIf = (cond) => (cond ? { color: ACTIVE_COLOR } : undefined);
  const isActiveReports = (cls) =>
    isActivePath([
      "/investor-relations/reports",
      "/yatirimci-iliskileri/raporlar",
    ]) && q.get("classfield") === cls;

  return (
    <aside className={`po-sidebar ${className}`}>
      <section className="po-sec">
        {/* Yatırımcı İlişkileri */}
        <button className="po-sec-h" onClick={() => toggleSection("ir")}>
          <span>{t("investor-relations")}</span>
          <CgChevronDown className={`po-caret ${openSections["ir"] ? "is-open" : ""}`} />
        </button>

        {/* IPO + Analyst Reports */}
        <ul className={`po-sub ${openSections["ir"] ? "is-open" : ""}`}>
          <li className="po-sub-group">
            <button className="po-sub-h" onClick={() => toggleSection("ipo")}>
              <span>{t("initial-public-offering-and-shares")}</span>
              <CgChevronDown className={`po-caret ${openSections["ipo"] ? "is-open" : ""}`} />
            </button>
            <ul className={`po-sub2 ${openSections["ipo"] ? "is-open" : ""}`}>
              <li>
                <Link
                  to="/investor-relations/initial-public-offering-new"
                  className="po-link"
                  style={styleIf(
                    isActivePath([
                      "/investor-relations/initial-public-offering-new",
                      "/yatirimci-iliskileri/halka-arz-yeni",
                    ])
                  )}
                >
                  {t("initial-public-offering")}
                </Link>
              </li>
              {/* <li>
                <Link
                  to="/investor-relations/analyst-reports"
                  className="po-link"
                  style={styleIf(
                    isActivePath([
                      "/investor-relations/analyst-reports",
                      "/yatirimci-iliskileri/analist-raporlari",
                    ])
                  )}
                >
                  {t("analyst-reports")}
                </Link>
              </li> */}
            </ul>
          </li>
        </ul>

        {/* Kurumsal Yönetim */}
        <ul className={`po-sub ${openSections["ir"] ? "is-open" : ""}`}>
          <li className="po-sub-group">
            <button className="po-sub-h" onClick={() => toggleSection("corp")}>
              <span>{t("corporate-governance")}</span>
              <CgChevronDown className={`po-caret ${openSections["corp"] ? "is-open" : ""}`} />
            </button>
            <ul className={`po-sub2 ${openSections["corp"] ? "is-open" : ""}`}>
              <li>
                <Link
                  to="/investor-relations/board-of-directors"
                  className="po-link"
                  style={styleIf(
                    isActivePath([
                      "/investor-relations/board-of-directors",
                      "/yatirimci-iliskileri/yonetim-kurulu",
                    ])
                  )}
                >
                  {t("board-of-directors")}
                </Link>
              </li>
              <li>
                <Link
                  to="/investor-relations/executive-committee"
                  className="po-link"
                  style={styleIf(
                    isActivePath([
                      "/investor-relations/executive-committee",
                      "/yatirimci-iliskileri/icra-kurulu",
                    ])
                  )}
                >
                  {t("executive-committee")}
                </Link>
              </li>
              {/* <li>
                <Link
                  to="/investor-relations/board-committees"
                  className="po-link"
                  style={styleIf(
                    isActivePath([
                      "/investor-relations/board-committees",
                      "/yatirimci-iliskileri/yonetim-kurulu-komiteleri",
                    ])
                  )}
                >
                  {t("committees-of-the-board-of-directors")}
                </Link>
              </li> */}
              <li>
                <Link
                  to="/investor-relations/trade-registry"
                  className="po-link"
                  style={styleIf(
                    isActivePath([
                      "/investor-relations/trade-registry",
                      "/yatirimci-iliskileri/ticaret-sicili",
                    ])
                  )}
                >
                  {t("trade-registry")}
                </Link>
              </li>
              <li>
                <Link
                  to="/investor-relations/general-assembly"
                  className="po-link"
                  style={styleIf(
                    isActivePath([
                      "/investor-relations/general-assembly",
                      "/yatirimci-iliskileri/genel-kurul",
                    ])
                  )}
                >
                  {t("general-assembly")}
                </Link>
              </li>
              {/* <li>
                <Link
                  to="/investor-relations/corporate-governance-report"
                  className="po-link"
                  style={styleIf(
                    isActivePath([
                      "/investor-relations/corporate-governance-report",
                      "/yatirimci-iliskileri/kurumsal-yonetim-raporu",
                    ])
                  )}
                >
                  {t("corporate-governance-report")}
                </Link>
              </li> */}
              <li>
                <Link
                  to="/investor-relations/articles-of-association"
                  className="po-link"
                  style={styleIf(
                    isActivePath([
                      "/investor-relations/articles-of-association",
                      "/yatirimci-iliskileri/esas-sozlesme",
                    ])
                  )}
                >
                  {t("articles-of-association")}
                </Link>
              </li>
              <li>
                <Link
                  to="/investor-relations/capital-structure"
                  className="po-link"
                  style={styleIf(
                    isActivePath([
                      "/investor-relations/capital-structure",
                      "/yatirimci-iliskileri/sermaye-yapisi",
                    ])
                  )}
                >
                  {t("capital-structure")}
                </Link>
              </li>
              <li>
                <Link
                  to="/investor-relations/policies"
                  className="po-link"
                  style={styleIf(
                    isActivePath([
                      "/investor-relations/policies",
                      "/yatirimci-iliskileri/politikalar",
                    ])
                  )}
                >
                  {t("policies")}
                </Link>
              </li>
            </ul>
          </li>
        </ul>

        {/* Finansal Sonuçlar */}
        <ul className={`po-sub ${openSections["ir"] ? "is-open" : ""}`}>
          <li>
            <Link
              to="/investor-relations/financial-results"
              className="po-sub-h po-sub-h--link"
              style={styleIf(
                isActivePath([
                  "/investor-relations/financial-results",
                  "/yatirimci-iliskileri/finansal-sonuclar",
                ])
              )}
            >
              <span>{t("financial-results")}</span>
            </Link>
          </li>
        </ul>

        {/* Yatırımcı sunumları */}
        <ul className={`po-sub ${openSections["ir"] ? "is-open" : ""}`}>
          <li>
            <Link
              to="/investor-relations/investor-presentations"
              className="po-sub-h po-sub-h--link"
              style={styleIf(
                isActivePath([
                  "/investor-relations/investor-presentations",
                  "/yatirimci-iliskileri/yatirimci-sunumlari",
                ])
              )}
            >
              <span>{t("investor-presentations")}</span>
            </Link>
          </li>
        </ul>

        {/* Raporlar (alt menülü) */}
        {/* <ul className={`po-sub ${openSections["ir"] ? "is-open" : ""}`}>
          <li className="po-sub-group">
            <button className="po-sub-h" onClick={() => toggleSection("reports")}>
              <span>{t("reports")}</span>
              <CgChevronDown className={`po-caret ${openSections["reports"] ? "is-open" : ""}`} />
            </button>
            <ul className={`po-sub2 ${openSections["reports"] ? "is-open" : ""}`}>
              <li>
                <Link
                  to="/investor-relations/reports?classfield=activity"
                  className="po-link"
                  style={styleIf(isActiveReports("activity"))}
                >
                  {t("activity-reports")}
                </Link>
              </li>
              <li>
                <Link
                  to="/investor-relations/reports?classfield=sustainability"
                  className="po-link"
                  style={styleIf(isActiveReports("sustainability"))}
                >
                  {t("sustainability-reports")}
                </Link>
              </li>
              <li>
                <Link
                  to="/investor-relations/reports?classfield=cg-compliance"
                  className="po-link"
                  style={styleIf(isActiveReports("cg-compliance"))}
                >
                  {t("corporate-governance-compliance-and-info")}
                </Link>
              </li>
              <li>
                <Link
                  to="/investor-relations/reports?classfield=sus-compliance"
                  className="po-link"
                  style={styleIf(isActiveReports("sus-compliance"))}
                >
                  {t("sustainability-compliance-reports")}
                </Link>
              </li>
              <li>
                <Link
                  to="/investor-relations/reports?classfield=cg-report"
                  className="po-link"
                  style={styleIf(isActiveReports("cg-report"))}
                >
                  {t("corporate-governance-reports")}
                </Link>
              </li>
              <li>
                <Link
                  to="/investor-relations/reports?classfield=credit-rating"
                  className="po-link"
                  style={styleIf(isActiveReports("credit-rating"))}
                >
                  {t("credit-rating-reports")}
                </Link>
              </li>
            </ul>
          </li>
        </ul> */}

        {/* Özel Durum Açıklamaları */}
        <ul className={`po-sub ${openSections["ir"] ? "is-open" : ""}`}>
          <li>
            <Link
              to="/investor-relations/special-announcements"
              className="po-sub-h po-sub-h--link"
              style={styleIf(
                isActivePath([
                  "/investor-relations/special-announcements",
                  "/yatirimci-iliskileri/ozel-durum-aciklamalari",
                ])
              )}
            >
              <span>{t("special-announcements")}</span>
            </Link>
          </li>
        </ul>

        {/* Basın Duyuruları */}
        {/* <ul className={`po-sub ${openSections["ir"] ? "is-open" : ""}`}>
          <li>
            <Link
              to="/investor-relations/press-announcements"
              className="po-sub-h po-sub-h--link"
              style={styleIf(
                isActivePath([
                  "/investor-relations/press-announcements",
                  "/yatirimci-iliskileri/basin-duyurulari",
                ])
              )}
            >
              <span>{t("press-announcements")}</span>
            </Link>
          </li>
        </ul> */}

        {/* SSS */}
        <ul className={`po-sub ${openSections["ir"] ? "is-open" : ""}`}>
          <li>
            <Link
              to="/investor-relations/faq"
              className="po-sub-h po-sub-h--link"
              style={styleIf(
                isActivePath([
                  "/investor-relations/faq",
                  "/yatirimci-iliskileri/sikca-sorulan-sorular",
                ])
              )}
            >
              <span>{t("faq")}</span>
            </Link>
          </li>
        </ul>
      </section>
    </aside>
  );
};

export default InvestorSidebar;
